import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Button,
} from "@mui/material";
import Table from "../../../components/table/table";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import TableHeaderTextField from "../../../components/table-header-text-filed/table-header-text-filed";
import TableHeaderSelectField from "../../../components/table-header-select-field/table-header-select-field";
import { useDispatch, useSelector } from "react-redux";
import { getInvoicesRequest } from "../../../redux/orders/slice";
import { splitCamelCaseToWords } from "../../../utils/Formatter";
import { ClearAllOutlined } from "@mui/icons-material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InvoiceLineItemsDialog from "./invoice-line-items-dialog";
import {
  dispatchInvoiceRequest,
  downloadInvoiceRequest,
} from "../../../utils/APICalls";
import ErrorNotification from "../../../components/error-notification/error-notification";
import Notification from "../../../components/notification/notification";
import Loading from "../../../components/loading/loading";
import dayjs from "dayjs";
import AdvancedDatePicker from "../../../components/advanced-date-picker/advanced-date-picker";
import DispatchDialog from "./dispatch-dialog";

interface InvoiceTableProps {
  isClosed: boolean;
}

export default function InvoiceTable({ isClosed }: InvoiceTableProps) {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({
    isClosed: isClosed,
    page: 1,
    pageSize: 10,
    clientName: "",
    subDivisionName: "",
    invoiceType: "",
    invoiceNumber: "",
    projectName: "",
    totalAmount: "",
    closedDate: "",
    sortColumn: "",
    sortOrder: "",
  });
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any[]>([]);

  const { loading, invoices } = useSelector((state: any) => state.orders);

  const rowCountRef = useRef(invoices?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (invoices?.totalCount !== undefined) {
      rowCountRef.current = invoices?.totalCount;
    }
    return rowCountRef.current;
  }, [invoices?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "clientName",
      headerName: "Client",
      flex: 1,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
          {params.row.clientName || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key={`clientName-${filterResetKey}`}
          field="clientName"
          sortField="clientname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "invoiceType",
      headerName: "Invoice Frequency",
      flex: 1,
      sortable: false,
      resizable: isClosed ? true : false,
      headerClassName: isClosed ? "" : "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
          {splitCamelCaseToWords(params.row.invoiceType) || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          key={`invoiceType-${filterResetKey}`}
          field="invoiceType"
          sortField="invoiceType"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
          values={["Individual", "MonthlyBatch", "Project", "SemiMonthlyBatch"]}
        />
      ),
    },
    ...(isClosed
      ? [
        {
          field: "invoiceNumber",
          headerName: "Invoice #",
          flex: 1,
          sortable: false,
          resizable: false,
          headerClassName: "no-separator",
          renderCell: (params: GridRenderCellParams<any>) => (
            <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
              {params.row.invoiceNumber || "000010-B"}
            </Typography>
          ),
          renderHeader: (params: any) => (
            <TableHeaderTextField
              key={`invoiceNumber-${filterResetKey}`}
              field="invoiceNumber"
              sortField="invoicenumber"
              headerName={params.colDef.headerName}
              width={params.colDef.computedWidth}
              showFilter={showTableFilter}
              onFilterSubmit={onFilterItems}
            />
          ),
        },
      ]
      : []),
    {
      field: "projectName",
      headerName: "Project Name",
      flex: 1,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ pt: 2 }}>
          {params.row.projectName || ""}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key={`projectName-${filterResetKey}`}
          field="projectName"
          sortField="projectname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    {
      field: "totalAmount",
      headerName: isClosed ? "Total Invoice Amount" : "Amount",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "bold", pt: 2 }}>
          ${parseFloat(params.row.totalAmount as any)?.toFixed(2) || "0.00"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key={`totalAmount-${filterResetKey}`}
          field="totalAmount"
          sortField="totalAmount"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterItems}
        />
      ),
    },
    ...(isClosed
      ? [
        {
          field: "closedAt",
          headerName: "Invoiced Date",
          flex: 1,
          sortable: false,
          valueFormatter: (value?: string) =>
            value ? dayjs(value).format("MM/DD/YYYY") : " ",
          renderHeader: (params: any) => (
            <AdvancedDatePicker
              key={`closedAt-${filterResetKey}`}
              field="closedDate"
              sortField="closedAt"
              headerName={params.colDef.headerName}
              width={params.colDef.computedWidth}
              showFilter={showTableFilter}
              onFilterSubmit={onFilterItems}
            />
          ),
        },
      ]
      : []),
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      isClosed,
      page: 1,
      pageSize: 10,
      clientName: "",
      subDivisionName: "",
      invoiceType: "",
      invoiceNumber: "",
      projectName: "",
      totalAmount: "",
      closedDate: "",
      sortColumn: "",
      sortOrder: "",
    });
  }, [isClosed]); // eslint-disable-line

  useEffect(() => {
    const dataWithIds = (invoices?.items || []).map(
      (item: any, index: number) => ({
        ...item,
        id: item.id,
      })
    );
    setRowData(dataWithIds);
    if (selectedInvoice) {
      const findInvoice = dataWithIds.find((data: any) => data?.id === selectedInvoice?.id);
      if (findInvoice) {
        setSelectedInvoice(findInvoice);
      }
    }
  }, [invoices]); // eslint-disable-line

  useEffect(() => {
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });

    dispatch(getInvoicesRequest(filters));
    /* eslint-disable */
  }, [filterParams]); /* eslint-enable */

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const onClearAllFilters = () => {
    setFilterParams({
      isClosed,
      page: 1,
      pageSize: 10,
      clientName: "",
      subDivisionName: "",
      invoiceType: "",
      invoiceNumber: "",
      projectName: "",
      totalAmount: "",
      closedDate: "",
      sortColumn: "",
      sortOrder: "",
    });
    setFilterResetKey((prev) => prev + 1);
  };

  const handleRowClick = (params: any) => {
    setSelectedInvoice(params.row);
    setShowInvoiceDialog(true);
  };

  const handleCloseDialog = () => {
    setShowInvoiceDialog(false);
    setSelectedInvoice(null);
  };

  const handleDispatchClick = () => {
    setShowDispatchModal(true);
  };

  const handleCloseDispatchModal = () => {
    setShowDispatchModal(false);
  };

  const confirmDispatch = async () => {
    setIsDispatching(true);
    setShowLoading(true);
    try {
      const payload = {
        invoiceIds: rowSelectionModel
      }
      const response = await dispatchInvoiceRequest(payload);

      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.status === 204
      ) {
        setShowDispatchModal(false);
        setRowSelectionModel([]);
        dispatch(getInvoicesRequest(filterParams));
        setSuccessMessage("Invoice dispatched successfully!");
        setShowSuccessNotification(true);
      } else {
        const errMsg =
          (response && (response as any).data?.detail) ||
          (response && (response as any).data?.message) ||
          "Failed to dispatch invoice. Please try again.";
        setErrorMessage(errMsg);
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("An unexpected error occurred. Please try again.");
      setShowErrorNotification(true);
    } finally {
      setIsDispatching(false);
      setShowLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    const invoiceIds = rowSelectionModel
      .filter((id) => id != null)
      .map((id) => String(id));
    if (!invoiceIds.length) return;

    setIsDownloading(true);
    setShowLoading(true);
    try {
      const response = await downloadInvoiceRequest(invoiceIds);

      if (response && response.status === 200) {
        const mimeType = response.headers["content-type"] || "application/pdf";
        const blob = new Blob([response.data], { type: mimeType });

        const contentDisposition = response.headers["content-disposition"];
        let fileName =
          invoiceIds.length === 1
            ? `Invoice-${rowData.find((row: any) => String(row.id) === invoiceIds[0])
              ?.invoiceNumber || invoiceIds[0]
            }`
            : "Invoices";
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch && fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", fileName);
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        setErrorMessage("Failed to download invoice. Please try again.");
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("An unexpected error occurred. Please try again.");
      setShowErrorNotification(true);
    } finally {
      setIsDownloading(false);
      setShowLoading(false);
    }
  };

  const onFilterItems = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else if (keys.includes("closedDate.Operator")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const checkOpenOrders = () => {
    const openOrderFilter = selectedRowData.filter(
      (data: any) => data?.hasOpenOrders
    );
    return openOrderFilter.length > 0 ? true : false;
  }

  const updateInvoicesData = () => {
    dispatch(getInvoicesRequest(filterParams));
  }

  return (
    <>
      <Box
        className="clients-header"
        sx={{
          mt: 3,
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          {!isClosed ? (
            <Button
              variant="text"
              disabled={
                rowSelectionModel.length === 0 || checkOpenOrders()
              }
              onClick={handleDispatchClick}
              sx={{
                fontWeight: "bold",
              }}
            >
              DISPATCH INVOICES
            </Button>
          ) : (
            <IconButton
              aria-label="download-invoice"
              disabled={rowSelectionModel.length === 0 || isDownloading}
              onClick={handleDownloadInvoice}
              sx={{
                color:
                  rowSelectionModel.length === 0
                    ? "text.disabled"
                    : "primary.main",
              }}
            >
              <DownloadOutlinedIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}
        </Box>
        <Box>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          rowData={rowData}
          rowCount={rowCount}
          loading={loading}
          columnDef={column}
          headerHeight={headerHeight}
          disableColumnMenu={true}
          disableColumnResize={isClosed ? false : true}
          paginationModel={paginationModel}
          onPaginationModelChange={(newPaginationModel: any) => {
            setPaginationModel(newPaginationModel);
            setFilterParams((prev) => ({
              ...prev,
              page: newPaginationModel.page + 1,
              pageSize: newPaginationModel.pageSize,
            }));
          }}
          checkboxSelection={true}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
            const selected = newRowSelectionModel.map((id: string) =>
              rowData.find((r: any) => r.id === id)
            );
            setSelectedRowData(selected);
          }}
          rowSelectionModel={rowSelectionModel}
          onRowClick={handleRowClick}
        />
      </Box>

      <InvoiceLineItemsDialog
        key={
          selectedInvoice?.id ||
          selectedInvoice?.invoiceId ||
          selectedInvoice?.invoiceNumber ||
          "empty"
        }
        showDialog={showInvoiceDialog}
        closeDialog={handleCloseDialog}
        invoiceDetails={selectedInvoice}
        isClosed={isClosed}
        invoiceId={selectedInvoice?.id || selectedInvoice?.invoiceId || ""}
        updateInvoices={updateInvoicesData}
      />

      <DispatchDialog
        show={showDispatchModal}
        close={handleCloseDispatchModal}
        title={`Are you sure you want to dispatch the ${rowSelectionModel.length} batch
      ${rowSelectionModel.length === 1 ? "invoice" : "invoices"}?`}
        isDispatching={isDispatching}
        onConfirmDispatch={confirmDispatch}
        isFromInvoiceTable={true}
      />

      {showLoading && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      <Notification
        show={showSuccessNotification}
        message={successMessage}
        onClose={() => setShowSuccessNotification(false)}
      />
    </>
  );
}
